import { inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, concatMap, from, map, of, switchMap, tap, toArray, withLatestFrom } from 'rxjs'

import {
  DataModelAttributeType,
  DataModelEntity,
  DataModelEntityType,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'
import { ProcessDataModelEntityToUIEntity } from '@flowx/designer/shared/entities/data-model.entity'
import { selectTemporaryDataModel } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { IError } from '../../api/api-http.service'
import { ProcessDataModelApiService } from '../../api/process-data-model.api'
import { ProjectDataModelApiService } from '../../api/project-data-model.api'
import { ToastMessageService } from '../../services/toast-message.service'
import { ProcessDataModelActions } from '../actions/process-data-model.actions'
import { ProjectDataModelActions } from '../actions/project-data-model.actions'

// Helper: Get referenced flowxUuids from an entity
function getReferencedUuids(entity: DataModelEntity): string[] {
  return (entity.attributes || [])
    .map((attr) => attr.reference?.targetFlowxUuid)
    .filter((uuid) => !!uuid)
}

// Helper: Topological sort for dependency order
function topoSort(entities: DataModelEntity[]): DataModelEntity[] {
  const uuidToEntity = new Map(entities.map((e) => [e.flowxUuid, e]))
  const visited = new Set()
  const visiting = new Set()
  const result = []
  function visit(e: DataModelEntity): void {
    if (visited.has(e.flowxUuid)) {
      return
    }
    if (visiting.has(e.flowxUuid)) {
      return
    }
    visiting.add(e.flowxUuid)
    getReferencedUuids(e)
      .filter((uuid) => uuidToEntity.has(uuid))
      .forEach((uuid) => visit(uuidToEntity.get(uuid)))
    visiting.delete(e.flowxUuid)
    visited.add(e.flowxUuid)
    result.push(e)
  }
  entities.forEach((e) => visit(e))
  return result
}

/**
 * Reloads entities from the backend after a successful keep.
 */
export const keepTemporaryDataModelReload$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.keepTemporaryDataModelSuccess),
      tap(({ resourceId }) =>
        console.log('[AI-DM] keepTemporaryDataModelSuccess → reloading entities for', resourceId)
      ),
      switchMap(({ resourceId }) => of(ProcessDataModelActions.loadEntities({ resourceId })))
    ),
  { functional: true }
)

/**
 * Persists AI-generated entities from the temporary data model to the backend.
 */
export const saveTemporaryDataModel$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    dataModelBuilderApiService = inject(ProcessDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProcessDataModelActions.keepTemporaryDataModel),
      tap(({ resourceId }) =>
        console.log('[AI-DM] keepTemporaryDataModel action received, resourceId:', resourceId)
      ),
      withLatestFrom(store.select(selectTemporaryDataModel)),
      switchMap(([{ resourceId }, temporaryDataModel]) => {
        console.log('[AI-DM] temporaryDataModel keys:', Object.keys(temporaryDataModel))
        console.log(
          '[AI-DM] temporaryDataModel["temporary"] length:',
          temporaryDataModel['temporary']?.length
        )

        const generated = (temporaryDataModel['temporary'] || []).filter(
          (e) => e.type === DataModelEntityType.GENERATED
        )
        const rootEntity = (temporaryDataModel['temporary'] || []).find((e) => e.root)
        console.log('[AI-DM] generated entities count:', generated.length)
        console.log('[AI-DM] rootEntity found:', !!rootEntity)

        const sorted = topoSort(generated as any)
        console.log(
          '[AI-DM] sorted entities:',
          sorted.map((e) => e.name)
        )

        const uuidMap = new Map<string, string>()

        if (sorted.length === 0) {
          console.log('[AI-DM] No generated entities to save, going to root attributes')
          if (rootEntity) {
            const attrsToAdd = (rootEntity.attributes as any[]).filter((attr) => attr.added)
            console.log('[AI-DM] root attrs to add:', attrsToAdd.length)
            if (attrsToAdd.length === 0) {
              return of(ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
            }
            return from(attrsToAdd).pipe(
              concatMap((attr) => {
                console.log('[AI-DM] Adding root attribute:', attr.name)
                return dataModelBuilderApiService.addAttributeToDataModelEntity(
                  { resourceType: ApplicationResourceType.Process, resourceId },
                  rootEntity.identifier,
                  {
                    type: attr.type as DataModelAttributeType,
                    name: attr.name,
                    multiInstance: attr.multiInstance,
                    reference: attr.reference,
                  }
                )
              }),
              toArray(),
              map(() => ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
            )
          }
          return of(ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
        }

        return from(sorted).pipe(
          concatMap((entity) => {
            const relinked = {
              ...entity,
              attributes: entity.attributes.map((attr) => {
                if (
                  attr.reference?.targetFlowxUuid &&
                  uuidMap.has(attr.reference.targetFlowxUuid)
                ) {
                  return {
                    ...attr,
                    reference: {
                      ...attr.reference,
                      targetFlowxUuid: String(uuidMap.get(attr.reference.targetFlowxUuid)),
                      targetResourceDefinitionId: null,
                      appId: null,
                    },
                  }
                }
                return attr
              }),
            }
            console.log('[AI-DM] Saving entity:', entity.name, 'flowxUuid:', entity.flowxUuid)
            return dataModelBuilderApiService
              .addDataModelEntity(
                { resourceType: ApplicationResourceType.Process, resourceId },
                relinked
              )
              .pipe(
                tap((saved) =>
                  console.log(
                    '[AI-DM] Entity saved:',
                    entity.name,
                    'new flowxUuid:',
                    mapResource(saved).flowxUuid
                  )
                ),
                map((saved) => {
                  uuidMap.set(entity.flowxUuid, mapResource(saved).flowxUuid)
                  return ProcessDataModelActions.addEntitySuccess({
                    resourceId,
                    entity: ProcessDataModelEntityToUIEntity(mapResource(saved)),
                  })
                }),
                catchError(({ error }: { error: IError }) => {
                  console.error('[AI-DM] Error saving entity:', entity.name, error)
                  toastMessageService.showErrorMessage('Add data model type failed', error?.title)
                  return of(ProcessDataModelActions.addEntityFailure({ resourceId }))
                })
              )
          }),
          toArray(),
          switchMap(() => {
            console.log('[AI-DM] All generated entities saved. Processing root attributes...')
            if (rootEntity) {
              const attrsToAdd = (rootEntity.attributes as any[])
                .filter((attr) => attr.added)
                .map((attr) => {
                  if (
                    attr.reference?.targetFlowxUuid &&
                    uuidMap.has(attr.reference.targetFlowxUuid)
                  ) {
                    return {
                      ...attr,
                      type: 'OBJECT',
                      reference: {
                        ...attr.reference,
                        targetFlowxUuid: uuidMap.get(attr.reference.targetFlowxUuid),
                        targetResourceDefinitionId: null,
                      },
                    }
                  }
                  return attr
                })
              const filteredAttrs = attrsToAdd.filter((a) => a.added)
              console.log('[AI-DM] Root attrs to add:', filteredAttrs.length)
              if (filteredAttrs.length === 0) {
                return of(ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
              }
              return from(filteredAttrs).pipe(
                concatMap((attr) => {
                  console.log(
                    '[AI-DM] Adding root attr:',
                    attr.name,
                    'to entity:',
                    rootEntity.identifier
                  )
                  return dataModelBuilderApiService.addAttributeToDataModelEntity(
                    { resourceType: ApplicationResourceType.Process, resourceId },
                    rootEntity.identifier,
                    {
                      type: attr.type as DataModelAttributeType,
                      name: attr.name,
                      multiInstance: attr.multiInstance,
                      reference: attr.reference,
                    }
                  )
                }),
                toArray(),
                map(() => ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
              )
            }
            return of(ProcessDataModelActions.keepTemporaryDataModelSuccess({ resourceId }))
          })
        )
      })
    ),
  { functional: true }
)

/**
 * Persists AI-generated entities from the temporary data model to the PROJECT data model backend.
 */
export const saveProjectTemporaryDataModel$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    projectDataModelApi = inject(ProjectDataModelApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(ProjectDataModelActions.keepProjectTemporaryDataModel),
      withLatestFrom(store.select(AiSelectors.generatedDataModel)),
      switchMap(([, generatedDataModel]) => {
        const aiEntities = generatedDataModel?.entities || []
        console.log('[AI-DM Project] keepProjectTemporaryDataModel, entities:', aiEntities.length)

        if (aiEntities.length === 0) {
          return of(ProjectDataModelActions.keepProjectTemporaryDataModelSuccess())
        }

        const sorted = aiEntities

        // Create each data type, then add its attributes (AI format: en, fl, fn, ft)
        return from(sorted).pipe(
          concatMap((aiEntity: any) =>
            projectDataModelApi
              .createDataType({
                name: aiEntity.en || aiEntity.ec || 'unnamed',
                description: aiEntity.ed || '',
              })
              .pipe(
                switchMap((response) => {
                  const savedDataType = mapResource(response)
                  const fields = (aiEntity.fl || []).filter((f: any) => f.fn && f.ft)
                  console.log(
                    '[AI-DM Project] Created:',
                    savedDataType.name,
                    'resourceId:',
                    savedDataType.resourceId,
                    'fields:',
                    fields.length
                  )
                  if (fields.length === 0) {
                    return of(savedDataType)
                  }
                  return from(fields).pipe(
                    concatMap((field: any) =>
                      projectDataModelApi.createAttribute(
                        { resourceId: savedDataType.resourceId },
                        {
                          name: field.fn,
                          type: field.ft,
                          multiInstance: !!field.fa,
                        } as any
                      )
                    ),
                    toArray(),
                    map(() => savedDataType)
                  )
                }),
                catchError(({ error }: { error: IError }) => {
                  console.log('[AI-DM Project] Error:', error)
                  toastMessageService.showErrorMessage('Add project data type failed', error?.title)
                  return of(null)
                })
              )
          ),
          toArray(),
          switchMap(() =>
            of(
              ProjectDataModelActions.keepProjectTemporaryDataModelSuccess(),
              ProjectDataModelActions.loadProjectDataModel()
            )
          )
        )
      })
    ),
  { functional: true }
)
